/**
 * DAVIDsTEA: clicking "Add" on one product card, then a different product card's "Add" before
 * the first request finishes, races two independent cart mutations against the same shared
 * cart-drawer-section morph — confirmed live to drop an item from the cart entirely and leave
 * the drawer showing a row count that didn't match the real cart. Every add-to-cart-component
 * already guards against being double-clicked itself (assets/product-form.js's
 * AddToCartComponent.disable/enable), but nothing stops a *different* button from firing while
 * the first is still in flight — there was no cross-component lock at all.
 *
 * This listens for the same cartLinesUpdate event every add/remove/quantity-change already
 * dispatches (assets/component-cart-items.js, assets/product-form.js) and disables every
 * add-to-cart button on the page for the duration of any one in-flight mutation, so a second
 * click simply can't start a second request until the first has fully resolved — the race
 * becomes impossible to trigger rather than something to resolve correctly after the fact.
 *
 * Only buttons this module itself disables are ever re-enabled: a button already disabled for
 * its own reason (sold out, no variant selected) before the lock started is left untouched,
 * so this can't accidentally make an unavailable product addable again.
 */
import { StandardEvents } from '@shopify/events';

/** @type {number} */
let activeMutations = 0;

/** @type {Set<Element>} */
let lockedButtons = new Set();

function lockAllAddButtons() {
  for (const button of document.querySelectorAll('add-to-cart-component')) {
    if (lockedButtons.has(button)) continue;
    if (typeof (/** @type {any} */ (button).disable) !== 'function') continue;

    const addToCartButton = /** @type {any} */ (button).refs?.addToCartButton;
    if (!addToCartButton || addToCartButton.disabled) continue;

    /** @type {any} */ (button).disable();
    lockedButtons.add(button);
  }
}

function unlockAllAddButtons() {
  for (const button of lockedButtons) {
    if (typeof (/** @type {any} */ (button).enable) === 'function') {
      /** @type {any} */ (button).enable();
    }
  }
  lockedButtons.clear();
}

document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
  const promise = /** @type {any} */ (event).promise;
  if (!promise) return;

  activeMutations += 1;
  lockAllAddButtons();

  promise
    .finally(() => {
      activeMutations = Math.max(0, activeMutations - 1);
      if (activeMutations === 0) unlockAllAddButtons();
    })
    .catch((/** @type {any} */ error) => {
      if (error?.name !== 'AbortError') console.warn('[dt-cart-add-lock] Event promise rejected:', error);
    });
});
